import { Component, EventEmitter, Input, Output, ViewChild, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { GlobalNotificationService } from '../../../shared/services/globalnotification.service';
import { UTCHelper } from '../../utils/UTCHelper';
import { UserViewModel, Permissions } from '../../interfaces/viewmodels/userviewmodel';
import { IORD_Order, IPatchOrderTaskDto, ITaskViewModel, PatchOrderTaskDto, PatchORDOrderOnHoldDto } from '../../interfaces/request.interface';
import { Store } from '@ngxs/store';
import { RequestState } from '../../store/state/request.state';
import { filter, map, Observable, Subject, take, takeUntil } from 'rxjs';
import { AccountState } from '../../store/state/account.state';
import { GridSize } from '@progress/kendo-angular-grid';
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';
import { RequestActions } from '../../store/action/request.action';
import { pencilIcon, rotateIcon, arrowRotateCwIcon, handIcon, saveIcon, xIcon, clockIcon, trashIcon, SVGIcon } from '@progress/kendo-svg-icons';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { OptionState } from '../../store/state/options.state';
import { SignalRService } from '../../services/signalr.service';
import { AdminService } from '../../services/admin.service';

@Component({
    selector: 'app-processtask-data',
    templateUrl: './processtask-data.component.html',
    styleUrl: './processtask-data.component.css',
    standalone: false
})
export class ProcesstaskDataComponent implements OnInit, OnDestroy {
  public showDiagRollbackTask: boolean = false;
  public showDiagRollbackSubTask: boolean = false;
  public showDiagRollbackNestedSubTask: boolean = false;
  public smallSize: GridSize = "small";
  public pencilIcon: SVGIcon = pencilIcon;
  public arrowRotateCwIcon: SVGIcon = arrowRotateCwIcon;
  public rotateIcon: SVGIcon = rotateIcon;
  public handIcon: SVGIcon = handIcon;
  public saveIcon: SVGIcon = saveIcon;
  public xIcon: SVGIcon = xIcon;
  public clockIcon: SVGIcon = clockIcon;
  public trashIcon: SVGIcon = trashIcon;
  public showOnHold: boolean = false;
  private destroy$ = new Subject<void>();
  public updatedStartDate?: Date;
  public updatedCompleteDate?: Date;

  // Approval task properties
  public showRejectReasonDialog: boolean = false;
  public rejectReason: string = '';
  private rejectTaskId: number = 0;

  // NC (Non-Conformance) dialog properties
  public showNCDialog: boolean = false;
  private ncPendingTask: ITaskViewModel | null = null;

  // Delete task/process dialog properties
  public showDeleteConfirmDialog: boolean = false;
  public deleteConfirmMessage: string = '';
  private deleteMode: 'task' | 'process' = 'task';
  private deleteTargetTaskId: number = 0;

  public holdDate: Date = new Date();

  public onHoldCategories$!: Observable<any[] | undefined>;

  public onHold_Form!: FormGroup;
  public onHold_TaskId: number = 0;


  taskViewModel: ITaskViewModel[] = [];
  reworkTaskViewModel: ITaskViewModel[] = [];
  rollbackTaskViewModel: ITaskViewModel = {} as ITaskViewModel;
  isLoadingTaskProcessing = false;
  startValue: Date | null = null;
  endValue: Date | null = null;
  onHold: boolean = false;
  showRework = false;
  listOfRework = [];
  ProcessUserAssignment: number = 0;

  public filterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: "contains",
  };


  @Input() draftId?: number;
  public request$!: Observable<IORD_Order | undefined>;
  public requestTasks$!: Observable<ITaskViewModel[] | undefined>;
  public employees!: Observable<{ id: number; name: string, loginID: string }[]>;


  public user$!: Observable<UserViewModel>;

  constructor(
    private notification: GlobalNotificationService, 
    private fb: FormBuilder,
    private store: Store,
    private signalRService: SignalRService,
    private cdr: ChangeDetectorRef,
    private adminService: AdminService
  ) {
      this.store.select(RequestState.orderTasks).pipe(
        filter(tasks => !!tasks && tasks.length > 0),
      ).subscribe(tasks => {
        console.log('🔄 Tasks state changed, reloading task view');
        this.loadTask();
      });
    this.employees = this.store.select(AccountState.users).pipe(
      map(dict =>
        Object.values(dict).map(vm => ({
          id: vm.id,
          name: vm.displayName,
          loginID: vm.loginID
        }))
      )
    );
  }
  ngOnInit(): void {
    this.onHoldCategories$ = this.store.select(OptionState.onHoldCategories);
    this.user$ = this.store.select(AccountState.user);
    this.store.select(RequestState.getById).pipe(
      takeUntil(this.destroy$),
      map(getByIdFn => getByIdFn(this.draftId ?? 0)),
    ).subscribe(request => {
      if (request) {
        this.onHold = request.onHold ?? false;
      }
    });
    this.requestTasks$ = this.store.select(RequestState.orderTasks).pipe(filter(tasks => !!tasks && tasks.length > 0),);
    this.loadTask();
    
    // Subscribe to SignalR events (connection is managed by parent EJDF/Edit component)
    this.subscribeToSignalREvents();
  }

  /**
   * Subscribe to SignalR events for real-time task updates.
   * Note: The SignalR connection is managed by the parent EJDF/Edit component.
   */
  private subscribeToSignalREvents(): void {
    // Subscribe to task picked up events
    this.signalRService.onTaskPickedUp
      .pipe(
        takeUntil(this.destroy$),
        filter(event => event !== null && event.taskId === this.draftId)
      )
      .subscribe(event => {
        console.log('✅ ProcessTask: Task picked up event received:', event);
        this.reloadTasks();
      });

    // Subscribe to task status changed events
    this.signalRService.onTaskStatusChanged
      .pipe(
        takeUntil(this.destroy$),
        filter(event => event !== null && event.taskId === this.draftId)
      )
      .subscribe(event => {
        console.log('✅ ProcessTask: Task status changed event received:', event);
        this.reloadTasks();
      });

    // Subscribe to task updated events
    this.signalRService.onTaskUpdated
      .pipe(
        takeUntil(this.destroy$),
        filter(event => event !== null && event.taskId === this.draftId)
      )
      .subscribe(event => {
        console.log('✅ ProcessTask: Task updated event received:', event);
        this.reloadTasks();
      });
  }

  /**
   * Reload tasks from the store and trigger change detection
   */
  private reloadTasks(): void {
    if (!this.draftId) return;
    
    console.log('🔄 Dispatching LoadTasks for order:', this.draftId);
    this.store.dispatch(new RequestActions.LoadTasks(this.draftId)).subscribe(() => {
      console.log('✅ LoadTasks completed, triggering change detection');
      this.cdr.detectChanges();
    });
  }

  loadTask() {
    this.requestTasks$?.subscribe(tasks => {
      var taskCount = 1;
      const _taskData = [...(tasks ?? [])];

      this.taskViewModel = _taskData?.map(origTask => {
        const task = { ...origTask };

        task.taskActive = (task?.startDate || task?.queue_Start) && !task?.completeDate;

        task.isLastTask = false;
        if (_taskData.length === taskCount) {
          task.isLastTask = true;
        }

        if (task.startDate) {
          const localstartDate = this.convertUTCDateToLocalDate(task.startDate);
          if (localstartDate) {
            task.startDate = localstartDate;
          }
        }

        if (task.completeDate) {
          const localcompleteDate = this.convertUTCDateToLocalDate(task.completeDate);
          if (localcompleteDate) {
            task.completeDate = localcompleteDate;
          }
        }

        if (task.kickedBack_Task_Id == null) {
          const node = { title: task.processName, key: task.id?.toString(), disableCheckbox: false };
          const childrenNodes: { title: string; key: string; disableCheckbox: boolean; isLeaf?: boolean }[] = [];

          if (task.queue_Start === null) {
            node.disableCheckbox = true;
          }

          if (task.subTasks) {
            let SubOrderTasks = 1;
            const lastActiveSubtask = task.subTasks.reduceRight((prev, curr) => {
              if (curr.completeDate === null && curr.queue_Start !== null) {
                return curr;
              }
              return prev;
            }, null as ITaskViewModel | null);

            task.subTasks = task.subTasks.map((subtask, index, arr) => ({
              ...subtask,
              canRollback: false,
              isLastTask: index === arr.length - 1,
              init: subtask.init ?? (() => { }),
              toJSON: subtask.toJSON ?? (() => JSON.stringify(subtask))
            }));
          }
        }

        taskCount++;
        return task;
      });
    });
    console.log('Loading tasks...');

    //this.nodes = _nodes;
    var tasks = 1;
    this.isLoadingTaskProcessing = false;
  }

  isTaskDateDisabled(task: ITaskViewModel, taskDate: string): boolean {
    if (taskDate === "Start" && task.queue_Start !== null && task.isEdit) { return false; }
    else if (taskDate === "Complete" && task.startDate !== null && task.isEdit) { return false; }
    else { return true; }
  }

  ShowComplete(): boolean {
    return false;
  }
  ShowEditTask(index: number) {
    if(this.taskViewModel[index]?.startDate)
    this.updatedStartDate = this.taskViewModel[index].startDate;
    this.updatedCompleteDate = this.taskViewModel[index].completeDate;
    this.employees
      .pipe(
        map(employees => employees.find(emp => emp.loginID === this.taskViewModel[index].loginID))
      )
      .subscribe(foundEmployee => {
        if (foundEmployee) {
          this.taskViewModel[index].user_Id = foundEmployee.id;
        } else {
          console.log('Employee not found.');
        }
      });
      
    this.taskViewModel[index].isEdit = true;
  }
  ShowOnHold(task: ITaskViewModel) {
    this.onHold_TaskId = task.id ?? 0;
    this.onHold_Form = this.fb.group({
      onHoldCat_Id: [null, Validators.required],
      reason: [null],
      order_Id: [this.draftId, Validators.required]
    });
    this.showOnHold = true;
  }
  StartTask(task: ITaskViewModel) {
    this.isLoadingTaskProcessing = true;
    if (task.id !== undefined && this.draftId !== undefined) {
      this.store.dispatch(new RequestActions.StartTask(task.id, this.draftId));
      this.store.select(RequestState.orderTasks).pipe(
        filter(tasks => !!tasks && tasks.length > 0),
      ).subscribe(tasks => {
        this.loadTask();
        this.isLoadingTaskProcessing = false;
      });
    } else {
      this.isLoadingTaskProcessing = false;
      console.error('Task ID is undefined or draftId is undefined.');
    }
  }

  getTaskOrdered(tasks: ITaskViewModel[]) {
    return tasks.sort(function (a, b) { return (a.sequence ?? 0) - (b.sequence ?? 0) })
  }

  /**
   * Check if this task is "Finalize RTA" under the "Sustaining" parent process.
   * When completing this task, the user must choose whether NC is required.
   */
  isFinalizeRTAInSustaining(task: ITaskViewModel): boolean {
    const taskName = (task.processName ?? '').trim();
    const parentName = (task.parentProcessName ?? '').trim();
    console.log(`Checking if task "${taskName}" with parent "${parentName}" is Finalize RTA in Sustaining`);
    return taskName === 'Finalize RTA' && parentName === 'Sustaining';
  }

  CompleteTask(task: ITaskViewModel) {
    console.log('Attempting to complete task:', task);
    // Intercept "Finalize RTA" within "Sustaining" to show NC choice dialog
    if (this.isFinalizeRTAInSustaining(task)) {
      this.ncPendingTask = task;
      this.showNCDialog = true;
      return;
    }
    this.executeCompleteTask(task);
  }

  /**
   * Called when user selects "NC Required" from the dialog.
   * Adds the NC task (template 19) then completes the Finalize RTA task.
   */
  onNCRequired() {
    if (!this.ncPendingTask || this.draftId === undefined) return;
    this.showNCDialog = false;
    this.isLoadingTaskProcessing = true;

    const task = this.ncPendingTask;
    this.ncPendingTask = null;

    // First add the NC task, then complete the Finalize RTA task
    this.store.dispatch(new RequestActions.AddNewTask(19, this.draftId, true)).subscribe({
      next: () => {
        this.executeCompleteTask(task);
      },
      error: (err) => {
        console.error('Error adding NC task:', err);
        this.isLoadingTaskProcessing = false;
        this.notification.showErrorNotification('Failed to add NC task. Finalize RTA was not completed.');
      }
    });
  }

  /**
   * Called when user selects "NC NOT Required" from the dialog.
   * Simply completes the Finalize RTA task without adding NC.
   */
  onNCNotRequired() {
    if (!this.ncPendingTask) return;
    this.showNCDialog = false;
    const task = this.ncPendingTask;
    this.ncPendingTask = null;
    this.executeCompleteTask(task);
  }

  /**
   * Cancel the NC dialog without completing the task.
   */
  cancelNCDialog() {
    this.showNCDialog = false;
    this.ncPendingTask = null;
  }

  /**
   * Determine if a task (sub-task row) can be deleted.
   * Only allowed if the task has not been started.
   */
  canDeleteTask(task: ITaskViewModel): boolean {
    return !task.startDate && !task.completeDate;
  }

  /**
   * Determine if a process (parent expansion panel) can be deleted.
   * Only allowed if neither the parent nor any child has been started.
   */
  canDeleteProcess(panel: ITaskViewModel): boolean {
    if (panel.startDate || panel.completeDate) return false;
    if (panel.subTasks && panel.subTasks.some(st => st.startDate || st.completeDate)) return false;
    return true;
  }

  /**
   * Show confirmation dialog for deleting a single sub-task.
   */
  showDeleteTaskConfirm(task: ITaskViewModel) {
    if (!task.id) return;
    this.deleteMode = 'task';
    this.deleteTargetTaskId = task.id;
    this.deleteConfirmMessage = `Are you sure you want to delete the task "${task.processName ?? 'Unknown'}"? This action cannot be undone.`;
    this.showDeleteConfirmDialog = true;
  }

  /**
   * Show confirmation dialog for deleting an entire process and all its sub-tasks.
   */
  showDeleteProcessConfirm(panel: ITaskViewModel) {
    if (!panel.id) return;
    this.deleteMode = 'process';
    this.deleteTargetTaskId = panel.id;
    const subTaskCount = (panel.subTasks ?? []).length;
    const subTaskMsg = subTaskCount > 0 ? ` and its ${subTaskCount} sub-task(s)` : '';
    this.deleteConfirmMessage = `Are you sure you want to delete the entire "${panel.processName ?? 'Unknown'}" process${subTaskMsg}? This action cannot be undone.`;
    this.showDeleteConfirmDialog = true;
  }

  /**
   * Cancel the delete confirmation dialog.
   */
  cancelDeleteConfirm() {
    this.showDeleteConfirmDialog = false;
    this.deleteConfirmMessage = '';
    this.deleteTargetTaskId = 0;
  }

  /**
   * Confirm and execute the delete action.
   */
  confirmDelete() {
    if (!this.deleteTargetTaskId || this.draftId === undefined) return;
    this.showDeleteConfirmDialog = false;

    if (this.deleteMode === 'task') {
      this.store.dispatch(new RequestActions.DeleteOrderTask(this.deleteTargetTaskId, this.draftId));
    } else {
      this.store.dispatch(new RequestActions.DeleteOrderProcess(this.deleteTargetTaskId, this.draftId));
    }

    this.deleteTargetTaskId = 0;
    this.deleteConfirmMessage = '';
  }

  /**
   * Execute the actual task completion logic.
   */
  private executeCompleteTask(task: ITaskViewModel) {
    this.isLoadingTaskProcessing = true;
    if (task.id !== undefined && this.draftId !== undefined) {
      this.store.dispatch(new RequestActions.CompleteTask(task.id, task.notes ?? '', this.draftId));
      if (this.draftId !== undefined) {
        this.store.select(RequestState.orderTasks).pipe(
          filter(tasks => !!tasks && tasks.length > 0),
        ).subscribe(tasks => {
          this.loadTask();
        });
        this.isLoadingTaskProcessing = false;
      }
    } else {
      this.isLoadingTaskProcessing = false;
      console.error('Task ID is undefined.');
    }
  }

  /**
   * Check if a task is a DBTM Approval or Country Manager Approval process.
   */
  isApprovalProcess(task: ITaskViewModel): boolean {
    const name = task.processName ?? '';
    return name === 'DBTM Approval' || name === 'Country Manager Approval';
  }

  /**
   * Check if the current user has permission to approve/reject a specific approval task.
   * Rules:
   *  - RTA_ADMIN can always approve.
   *  - DBTM Approval requires DBTM_APPROVE_RTA permission.
   *  - Country Manager Approval requires COUNTRY_MANAGER_APPROVE_RTA permission.
   *  - The request's subapplication must be in the user's assigned subapplications.
   */
  canApproveTask(task: ITaskViewModel): boolean {
    if (!this.isApprovalProcess(task)) return false;

    // RTA_ADMIN bypasses all checks
    if (this.adminService.CheckPermission(Permissions.RTA_ADMIN)) return true;

    // Check the task-specific permission
    const processName = task.processName ?? '';
    let hasPermission = false;
    if (processName === 'DBTM Approval') {
      hasPermission = this.adminService.CheckPermission(Permissions.DBTM_APPROVE_RTA);
    } else if (processName === 'Country Manager Approval') {
      hasPermission = this.adminService.CheckPermission(Permissions.COUNTRY_MANAGER_APPROVE_RTA);
    }
    if (!hasPermission) return false;

    // Check subapplication assignment
    const user = this.store.selectSnapshot(AccountState.user);
    const assignedSubApps = user?.AssignedSubApplications ?? [];
    if (assignedSubApps.length === 0) return false;

    const getByIdFn = this.store.selectSnapshot(RequestState.getById);
    const request = getByIdFn ? getByIdFn(this.draftId ?? 0) : undefined;
    const requestAppId = request?.applicationId;
    if (!requestAppId) return false;

    return assignedSubApps.some(sa => sa.subApplication_Id === requestAppId);
  }

  /**
   * Approve an approval task (DBTM Approval / Country Manager Approval).
   */
  ApproveTask(task: ITaskViewModel) {
    if (!this.canApproveTask(task)) {
      this.notification.showErrorNotification('You do not have permission to approve this task.');
      return;
    }
    this.isLoadingTaskProcessing = true;
    if (task.id !== undefined && this.draftId !== undefined) {
      this.store.dispatch(new RequestActions.ApproveOrderTask(task.id, this.draftId));
      this.store.select(RequestState.orderTasks).pipe(
        filter(tasks => !!tasks && tasks.length > 0),
      ).subscribe(tasks => {
        this.loadTask();
        this.isLoadingTaskProcessing = false;
      });
    } else {
      this.isLoadingTaskProcessing = false;
      console.error('Task ID or draftId is undefined.');
    }
  }

  /**
   * Show rejection dialog for an approval task.
   */
  showRejectDialog(task: ITaskViewModel) {
    if (!this.canApproveTask(task)) {
      this.notification.showErrorNotification('You do not have permission to reject this task.');
      return;
    }
    this.rejectTaskId = task.id ?? 0;
    this.rejectReason = '';
    this.showRejectReasonDialog = true;
  }

  /**
   * Cancel the rejection dialog.
   */
  cancelRejectDialog() {
    this.showRejectReasonDialog = false;
    this.rejectReason = '';
    this.rejectTaskId = 0;
  }

  /**
   * Confirm rejection of an approval task with the given reason.
   */
  confirmRejectTask() {
    if (!this.rejectReason.trim() || this.rejectTaskId === 0 || this.draftId === undefined) return;

    this.isLoadingTaskProcessing = true;
    this.store.dispatch(new RequestActions.RejectOrderTask(this.rejectTaskId, this.rejectReason, this.draftId));
    this.showRejectReasonDialog = false;
    this.rejectReason = '';
    this.rejectTaskId = 0;

    this.store.select(RequestState.orderTasks).pipe(
      filter(tasks => !!tasks && tasks.length > 0),
    ).subscribe(tasks => {
      this.loadTask();
      this.isLoadingTaskProcessing = false;
    });
  }
  UpdateTask(task: ITaskViewModel) {
    const updatedTask: IPatchOrderTaskDto = {
      ...task
    }
    if (this.updatedStartDate !== null && this.updatedStartDate !== undefined) {
      updatedTask.startDate = this.convertLocalDateToUTCDate(this.updatedStartDate);
    }
    if (this.updatedCompleteDate) {
      updatedTask.completeDate = this.convertLocalDateToUTCDate(this.updatedCompleteDate);
    }
    this.store.dispatch(new RequestActions.UpdateOrderTask({key: this.draftId ?? 0, task: updatedTask}));
    task.isEdit = false;
  }
  showRollback(task: ITaskViewModel): boolean {
    if ((task?.queue_Start && !task?.completeDate && !task.isLastTask) || (task?.queue_Start && task.isLastTask))
      return true;
    else return false;
  }
  showRollbacksubTask(task: ITaskViewModel): boolean {
    if ((task?.queue_Start && !task?.completeDate && !task.isLastTask) || (task?.queue_Start && task.isLastTask))
      return task?.subTasks ? task.subTasks.some(x => x?.startDate) : false
    else
      return false;
  }

  RollbackTask(task: ITaskViewModel) {
    this.isLoadingTaskProcessing = true;
    if (task.id !== undefined) {
      this.store.dispatch(new RequestActions.RollbackOrderTask(task.id, this.draftId ?? 0));
      this.loadTask();
      this.isLoadingTaskProcessing = false;
    } else {
      console.error('Task ID is undefined.');
      this.isLoadingTaskProcessing = false;
    }
  }

  RollBackSubTask(task: ITaskViewModel) {
    this.isLoadingTaskProcessing = true;
    if (task.id !== undefined) {
      this.store.dispatch(new RequestActions.RollbackSubOrderTask(task.id, this.draftId ?? 0));
      this.loadTask();
      this.isLoadingTaskProcessing = false;
    } else {
      console.error('Task ID is undefined.');
      this.isLoadingTaskProcessing = false;
    }
  }
  GetRequiredSubOrderTasks(task: ITaskViewModel[]) {
    return task.filter(x => !x.isParallel && x.kickedBack_Task_Id == null).sort(function (a, b) { return (a.sequence ?? 0) - (b.sequence ?? 0); });
  }
  GetNonRequiredSubOrderTasks(task: ITaskViewModel[]) {
    return task.filter(x => x.isParallel && x.kickedBack_Task_Id == null).sort(function (a, b) { return (a.sequence ?? 0) - (b.sequence ?? 0) });
  }
  getTimeSpanofOnHold(start?: string | Date, removed?: string | Date) {
    if (!start || !removed) {
      return "00d:00h:00m:00s";
    }
    const StartTime = new Date(start);
    const RemovedTime = new Date(removed);
    const msBetweenDates = RemovedTime.getTime() - StartTime.getTime();
    return this.convertMsToTime(msBetweenDates)
  }
  confirmRework(task: ITaskViewModel) {
    this.reworkTaskViewModel = this.taskViewModel.filter(x => (x.sequence ?? 0) <= (task.sequence ?? 0));
    this.showRework = true;
  }
  processRework() {
    var selectedProcessesForRework: number[] = [];
    // this.nzTreeComponent.getCheckedNodeList().forEach(x => {
    //   selectedProcessesForRework.push(parseInt(x.key));
    // })
    if (this.draftId !== undefined) {
      this.store.dispatch(new RequestActions.ProcessRework(this.draftId, selectedProcessesForRework));
      this.loadTask();
    }
  }
  hasSubtask(task: ITaskViewModel[]) {
    return task.length > 0 ? true : false;
  }
  getTaskHeader(task: ITaskViewModel) {
    if (task.isRework)
      return `${task.processName} (Rework)`;
    else
      return task.processName;
  }
  setTaskRollback(task: ITaskViewModel, taskModel: string) {
    this.rollbackTaskViewModel = task;
    switch (taskModel) {
      case 'task':
        this.showDiagRollbackTask = true;
        break;
      case 'subTask':
        this.showDiagRollbackSubTask = true;
        break;
    }
  }
  CancelOnHold(): void {
    this.showOnHold = false;
  }

  handleonholdOk(): void {
    const onHoldData: PatchORDOrderOnHoldDto = this.onHold_Form.value;
    this.store.dispatch(new RequestActions.ProcessOnHold(this.onHold_TaskId, onHoldData));
    this.showOnHold = false;
    this.store.select(RequestState.orderTasks).pipe(
      filter(tasks => !!tasks && tasks.length > 0),
    ).subscribe(tasks => {
      this.loadTask();
    });
  }
  convertMsToTime(milliseconds: number) {
    let seconds = Math.floor(milliseconds / 1000);
    let minutes = Math.floor(seconds / 60);
    let hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    seconds = seconds % 60;
    minutes = minutes % 60;
    hours = hours % 24;

    return `${this.padTo2Digits(days)}d:${this.padTo2Digits(hours)}h:${this.padTo2Digits(minutes)}m:${this.padTo2Digits(
      seconds,
    )}s`;
  }
  convertUTCDateToLocalDate(date: Date): Date | null {
    if (date == null || date == undefined)
      return null;
    const newDate = new Date(date.toString());
    let offset = newDate.getTimezoneOffset() * 60 * 1000;
    let localDate = new Date(newDate.getTime() - offset);
    return localDate;
  }
  convertLocalDateToUTCDate(date: Date): Date | undefined  {
    if (date == null || date == undefined)
      return undefined ;
    const newDate = new Date(date.toString());
    let offset = newDate.getTimezoneOffset() * 60 * 1000;
    let localDate = new Date(newDate.getTime() + offset);
    return localDate;
  }
  convertUTC(date: Date) {
    return UTCHelper.convertUTCDateToLocalDate(date);
  }
  padTo2Digits(num: number) {
    return num.toString().padStart(2, '0');
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Disconnect SignalR when component is destroyed
    this.signalRService.stopConnection();
  }
}
